import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, Clock, Link, Link2Off, LogOut, Mic, MicOff, Volume2, X } from 'lucide-react';
import type { VoiceApi } from '../useVoiceChat';

type MicState = 'off' | 'on' | 'muted' | 'connecting' | 'reconnecting' | 'failed' | 'listening';

/**
 * One microphone toggle carrying the whole state (off / connecting / on / muted / failed /
 * listening) plus a small overflow menu for the rarer actions, so the camp's toolbars stay
 * one row. Ported from the sibling secret-hitler app's VoiceControls.
 */
export function VoiceControls({ voice, players, you, compact = false }: {
  voice: VoiceApi;
  players: { id: string; name: string }[];
  you: string;
  /** The game HUD is tight: drop the lobby's written label and keep the two buttons. */
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const joined = voice.status === 'joined';
  const joining = voice.status === 'joining' || voice.rejoining;
  const connections = Object.values(voice.connections);
  const anyFailed = connections.includes('failed');
  const connectedCount = connections.filter((connection) => connection === 'connected').length;
  const others = voice.participants.filter((participant) => participant.playerId !== you).length;
  // Nobody can hear you until at least one peer is connected: say so on the primary control.
  const unheard = joined && others > 0 && connectedCount === 0;
  // "Listen only" is the entry choice; a courier who joined with a microphone and mutes is "Muted".
  const listenOnly = joined && voice.muted && !voice.intentMicrophone && !voice.everSpoke;
  const micOn = joined && !voice.muted;
  const state: MicState = joining ? 'connecting' : anyFailed ? 'failed' : unheard ? 'reconnecting'
    : listenOnly ? 'listening' : joined ? (micOn ? 'on' : 'muted') : 'off';
  const label = {
    off: 'Voice off',
    connecting: voice.rejoining ? 'Rejoining voice' : 'Connecting voice',
    reconnecting: `Connecting to ${others === 1 ? 'your friend' : 'your friends'}`,
    on: 'Mic on', muted: 'Muted', failed: 'Voice needs attention', listening: 'Listen only',
  }[state];
  const unavailable = voice.unsupported ?? (!voice.enabled ? 'Voice chat is switched off on this server.'
    : !voice.ready ? 'Reconnect to your camp to join voice.' : null);
  const count = voice.participants.length;
  const nameOf = (id: string) => players.find((player) => player.id === id)?.name ?? 'Friend';
  const speakingNow = voice.participants
    .filter((participant) => voice.speaking[participant.playerId] && participant.playerId !== you)
    .map((participant) => nameOf(participant.playerId));
  const heard = joined && others > 0 ? `${connectedCount} of ${others} connected` : null;

  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(false); };
    const key = (event: KeyboardEvent) => { if (event.key === 'Escape') { setOpen(false); trigger.current?.focus(); } };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('pointerdown', away); document.removeEventListener('keydown', key); };
  }, [open]);

  // Primary tap: off → join with mic; joined → toggle mute; failed → reconnect.
  const primary = () => {
    if (unavailable) { setOpen(true); return; }
    if (state === 'failed') { void voice.reconnect(); return; }
    if (state === 'connecting') return;
    if (!joined) { void voice.join(true); return; }
    if (voice.busy) return;
    void voice.toggleMute();
  };
  const Glyph = state === 'failed' ? Link2Off : state === 'connecting' || state === 'reconnecting' ? Clock
    : listenOnly ? Volume2 : micOn || state === 'off' ? Mic : MicOff;

  return (
    <div className={`voice-cluster voice-cluster--${state}${compact ? ' voice-cluster--compact' : ''}`} ref={root} role="group" aria-label="Voice chat">
      <button type="button" className={`icon-button voice-mic voice-mic--${state}${voice.speaking[you] && micOn ? ' is-speaking' : ''}`}
        aria-label={`Voice chat · ${label}${count ? ` · ${count} in voice` : ''}${voice.error ? ` · ${voice.error}` : ''}`}
        aria-pressed={joined ? micOn : undefined}
        title={voice.error ?? (heard ? `${label} · ${heard}` : label)}
        aria-busy={voice.busy || state === 'connecting'} onClick={primary}>
        <Glyph size={17} aria-hidden="true" />
        {count > 0 && <span className="voice-mic-count" aria-hidden="true">{count}</span>}
        {joined && others > 0 && connectedCount < others && connectedCount > 0 && <span className="voice-mic-dot" aria-hidden="true" />}
      </button>
      {!compact && <span className="voice-label" aria-hidden="true">{label}</span>}
      <button type="button" ref={trigger} className="icon-button voice-more" aria-label="Voice options"
        aria-haspopup="true" aria-expanded={open} aria-controls={menuId} onClick={() => setOpen((value) => !value)}>
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      <span className="sr-only" role="status">
        {joined ? `Voice: ${label}.` : voice.rejoining ? 'Voice: rejoining after the connection returned.' : ''}
        {voice.error ? ` ${voice.error}` : ''}
      </span>

      {open && (
        <div className="voice-menu paper-panel" id={menuId} role="group" aria-label="Voice options">
          <p className="voice-menu-state">
            <strong>{label}</strong>
            <span>{unavailable ?? (joined
              ? `${count} in voice${heard ? ` · ${heard}` : ''}${speakingNow.length ? ` · ${speakingNow.join(', ')} speaking` : ''}`
              : count ? `${count} already in voice` : 'Nobody in voice yet')}</span>
          </p>
          {voice.relay === 'none' && joined && <p className="voice-menu-note">
            <Link size={12} aria-hidden="true" />No relay configured: friends on strict mobile networks may not connect.
          </p>}
          {!joined && !joining && <>
            <button type="button" className="voice-menu-item" disabled={Boolean(unavailable)}
              onClick={() => { setOpen(false); void voice.join(true); }}><Mic size={16} aria-hidden="true" />Join with microphone</button>
            <button type="button" className="voice-menu-item" disabled={Boolean(unavailable)}
              onClick={() => { setOpen(false); void voice.join(false); }}><Volume2 size={16} aria-hidden="true" />Listen only</button>
          </>}
          {joining && <button type="button" className="voice-menu-item"
            onClick={() => { setOpen(false); voice.leave(); }}><X size={16} aria-hidden="true" />Cancel</button>}
          {joined && <>
            <button type="button" className="voice-menu-item" disabled={voice.busy} onClick={() => void voice.toggleMute()}>
              {voice.muted ? <Mic size={16} aria-hidden="true" /> : <MicOff size={16} aria-hidden="true" />}
              {voice.muted ? 'Unmute microphone' : 'Mute microphone'}
            </button>
            {anyFailed && <button type="button" className="voice-menu-item"
              onClick={() => { setOpen(false); void voice.reconnect(); }}><Link size={16} aria-hidden="true" />Reconnect voice</button>}
            {voice.playbackBlocked && <button type="button" className="voice-menu-item"
              onClick={() => void voice.resumeAudio()}><Volume2 size={16} aria-hidden="true" />Enable audio</button>}
            <button type="button" className="voice-menu-item"
              onClick={() => { setOpen(false); voice.leave(); }}><LogOut size={16} aria-hidden="true" />Leave voice</button>
          </>}
          {voice.error && <p className="voice-menu-error" role="alert">{voice.error}</p>}
          {joined && count > 1 && <ul className="voice-menu-roster" aria-label="Friends in voice">
            {voice.participants.map((participant) => {
              const own = participant.playerId === you;
              const connection = voice.connections[participant.playerId];
              const muted = own ? voice.muted : participant.muted;
              const status = muted ? (own && listenOnly ? 'listen only' : 'muted')
                : voice.speaking[participant.playerId] && (!own || connectedCount > 0) ? 'speaking' : 'mic on';
              return (
                <li key={participant.playerId}>
                  <span>{nameOf(participant.playerId)}{own && ' (you)'}</span>
                  <span>{status}{!own && connection && connection !== 'connected' ? ` · ${connection}` : ''}</span>
                </li>
              );
            })}
          </ul>}
          <p className="voice-menu-foot">Live only; nothing is recorded.</p>
        </div>
      )}
    </div>
  );
}

/** One in-flow line when the browser refuses autoplay: it never floats over the trail controls. */
export function VoicePlaybackNotice({ voice }: { voice: VoiceApi }) {
  if (!voice.playbackBlocked) return null;
  return (
    <div className="voice-playback-notice paper-panel" role="status">
      <Volume2 size={18} aria-hidden="true" />
      <strong>Your browser paused voice audio.</strong>
      <button type="button" className="text-button" onClick={() => void voice.resumeAudio()}>Enable audio</button>
    </div>
  );
}
